'use client';

import { AxiosError } from 'axios';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useMutateSubmitSalesBookingForm } from '@/hooks/mutation/useMutateSubmitSalesBookingForm';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { invalidateCurrentUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import { getHubSpotTrackingCookie } from '@/utils/hubspot/getHubSpotTrackingCookie';
import { mapOrganisationSizeToHubSpot } from '@/utils/mapOrganisationSizeToHubSpot';
import { toast } from '@heidi/design-system/toast';
import { SalesBookingFormName } from '@heidi/schema/ml-scribe';

import {
  trialUpgradeRequestCallbackModalMessages,
  type TrialUpgradeRequestCallbackModalFormValues,
} from '@heidi/commerce';
import { useHasTrialUpgradeCallbackRequested } from './useHasTrialUpgradeCallbackRequested';

const PAGE_NAME = 'Trial upgrade — request a call back';

/**
 * Submits the {@link TrialUpgradeRequestCallbackModal} form via the
 * `sales-booking/{form_name}` backend (server-side proxy to HubSpot) and
 * surfaces success / failure toasts. Returns `{ ok }` so the caller can
 * close the modal on success and stay open on failure.
 */
export function useTrialUpgradeRequestCallbackForm() {
  const intl = useIntl();
  const { data: user } = useQueryUser();
  const hasCallbackRequested = useHasTrialUpgradeCallbackRequested();
  const { mutateAsync: submitSalesBookingForm, isPending: isSubmitting } =
    useMutateSubmitSalesBookingForm();

  const onSubmit = useCallback(
    async (
      data: TrialUpgradeRequestCallbackModalFormValues
    ): Promise<{ ok: boolean }> => {
      if (!user || hasCallbackRequested) return { ok: false };

      const fields: Array<{ name: string; value: string }> = [
        { name: 'firstname', value: user.first_name ?? '' },
        { name: 'lastname', value: user.last_name ?? '' },
        { name: 'email', value: user.email ?? '' },
        { name: 'phone', value: data.phoneNumber },
        { name: 'contact_country', value: user.country || 'Not provided' },
        { name: 'company', value: user.organisation_info?.name ?? '' },
        {
          name: 'organization_size',
          value:
            mapOrganisationSizeToHubSpot(user.organisation_info?.size) ?? 'Individual',
        },
      ];

      const trimmedMessage = data.message?.trim();
      if (trimmedMessage) {
        fields.push({ name: 'message', value: trimmedMessage });
      }

      try {
        const response = await submitSalesBookingForm({
          formName: SalesBookingFormName.clinician_trial_upgrade,
          body: {
            fields,
            context: {
              page_name: PAGE_NAME,
              page_uri: typeof window !== 'undefined' ? window.location.href : null,
              hutk: getHubSpotTrackingCookie() ?? null,
            },
          },
        });

        const trialExtended = response.result?.trial_extended === true;

        if (trialExtended) {
          void invalidateCurrentUserBillingInfo();
        }

        toast({
          title: intl.formatMessage(
            trialUpgradeRequestCallbackModalMessages.requestSentSuccess
          ),
          description: trialExtended
            ? intl.formatMessage(
                trialUpgradeRequestCallbackModalMessages.requestSentTrialExtended
              )
            : undefined,
          tone: 'success',
          duration: 10000, // 10 seconds
        });
        return { ok: true };
      } catch (error) {
        // axios surfaces request timeouts as ECONNABORTED — keep the dedicated
        // timeout copy for that case, fall back to the generic error otherwise.
        const isTimeout = error instanceof AxiosError && error.code === 'ECONNABORTED';

        toast({
          title: intl.formatMessage(
            isTimeout
              ? trialUpgradeRequestCallbackModalMessages.requestFailedTimeout
              : trialUpgradeRequestCallbackModalMessages.requestFailedGeneric
          ),
          tone: 'destructive',
        });
        return { ok: false };
      }
    },
    [hasCallbackRequested, intl, submitSalesBookingForm, user]
  );

  return { onSubmit, isSubmitting, hasCallbackRequested };
}
